import React, { useEffect, useState } from 'react';
import { SharePointData, ExcludedItems } from '../../types/sharepoint.types';
import { cancelPipeline } from '../../services/api/virtualAssistant';

import { useSharePointData } from './useSharePointData';
import { useSharePointConnection } from './useSharePointConnection';

import SharePointHeader from './SharePointHeader';
import SharePointSidebar from './SharePointSidebar';
import SharePointConnectionTab from './SharePointConnectionTab';
import SharePointPreviewTab from './SharePointPreviewTab';
import SharePointFooter from './SharePointFooter';
import SharePointModals from './SharePointModals';

type VisibilityKey = 'lists' | 'document_libraries';

interface SharePointProps {
  setSharePointModalOpen: (isOpen: boolean) => void;
  sharepoints: SharePointData[];
  setSharepoints: React.Dispatch<React.SetStateAction<SharePointData[]>>;
  updateExcludedItems: (sharepointId: number, type: keyof ExcludedItems, itemId: string) => void;
  handleToggleChange: (key: VisibilityKey, sharepointId: number) => void;
  assistant: any;
  triggerAutoSave: () => void;
  isSharePointRNightly: boolean;
  setIsSharePointRNightly: React.Dispatch<React.SetStateAction<boolean>>;
  isSharePointFullLoad: boolean;
  setIsSharePointFullLoad: React.Dispatch<React.SetStateAction<boolean>>;
}

const SharePoint: React.FC<SharePointProps> = ({
  setSharePointModalOpen,
  sharepoints,
  setSharepoints,
  updateExcludedItems,
  handleToggleChange,
  assistant,
  triggerAutoSave,
  isSharePointRNightly,
  setIsSharePointRNightly,
  isSharePointFullLoad,
  setIsSharePointFullLoad
}) => {
  const [pendingSave, setPendingSave] = useState(false);
  const [isModalLocked, setIsModalLocked] = useState(false); // NEW: Prevent modal closing
  const [cancellingPipeline, setCancellingPipeline] = useState(false);

  // Move these state declarations BEFORE the hooks that use them
  const [connectedSharePointIds, setConnectedSharePointIds] = useState<string[]>([]);
  const [connectUsing, setConnectUsing] = useState<string>('loop');

  const [customModal, setCustomModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: "success" | "error" | "loading" | "confirmation" | "warning";
    onConfirm?: () => void;
  }>({
    isOpen: false,
    title: "",
    message: "",
    type: "confirmation",
  });

  const {
    selectedSharePoint,
    setSelectedSharePoint,
    activeSubTabs,
    setActiveSubTabs,
    expandedGetData,
    setExpandedGetData,
    expandedRows,
    showDocumentCache,
    isLoadingTabs,
    setIsLoadingTabs,
    checkedItems,
    setCheckedItems,
    contextMenu,
    setContextMenu,
    modalRefreshNModal,
    setModalRefreshNModal,
    lastSaveTarget,
    systemConfig,
    selectedSharepointData,
    addNewSharePoint,
    handleUrlChange,
    handleContextMenu,
    handleCheckboxChange,
    handleInsideToggleChange,
    moveSharePoint,
    deleteSharePoint,
    handleDragStart,
    handleDragEnter,
    handleDragLeave,
    handleDragEnd,
    handleDragOver,
    setActiveTab,
    handleNameChange,
    toggleExpandRow,
    handleItemClick,
    handleBackToLists,
    handleNavigateNext,
    handleNavigatePrevious,
    handlePageSizeChange,
    handleSearch,
    handleDocumentExclusion,
    handleLibraryExclusion,
    isDocumentExcluded,
    getLibrarySelectionState
  } = useSharePointData({
    sharepoints,
    setSharepoints,
    updateExcludedItems,
    handleToggleChange,
    assistant,
    triggerAutoSave,
    isSharePointFullLoad,
    setPendingSave,
    connectedSharePointIds,
    setConnectedSharePointIds
  });

  useEffect(() => {
    console.log('🚨 PARENT sharepoints changed:', sharepoints.length);
    console.log('🚨 PARENT SharePoint[0] excludedItems:', sharepoints[0]?.excludedItems?.excluded_document_libraries || []);
  }, [sharepoints]);

  const {
    pipelineData,
    currentStatus,
    gettingData,
    isRefreshing,
    handleConnect,
    handleGetDataNow,
    handleRefreshSharePointData
  } = useSharePointConnection({
    sharepoints,
    setSharepoints,
    setCheckedItems,
    setIsLoadingTabs,
    assistant,
    triggerAutoSave,
    isSharePointFullLoad,
    connectUsing,
    setConnectUsing,
    connectedSharePointIds,
    setConnectedSharePointIds,
    setPendingSave,
    isGettingDataCalled: true,
    setActiveTab,
    setCancellingPipeline
  });

  // Cancel pipeline function
  const handleCancelPipeline = async () => {
    try {
      setCancellingPipeline(true);
      // Note: currentStatus update should be handled in useSharePointConnection hook

      await cancelPipeline("sharepoint_to_vectordb", assistant.app_id);

      // The hook should handle status polling after cancel request
    } catch (error) {
      console.error('Failed to cancel pipeline:', error);
      setCancellingPipeline(false);
      
      setCustomModal({
        isOpen: true,
        title: "Cancel Failed",
        message: "Failed to cancel the operation. Please try again.",
        type: "error",
        onConfirm: (e?: React.MouseEvent) => {
          if (e) {
            e.preventDefault();
            e.stopPropagation();
          }
          setCustomModal(prev => ({ ...prev, isOpen: false }));
        }
      });
    }
  };

  const showConfirmationModal = (message: string, title: string, onConfirm: () => void) => {
    setCustomModal({
      isOpen: true,
      title: title,
      message,
      type: "confirmation",
      onConfirm: (e?: React.MouseEvent) => {
        if (e) {
          e.preventDefault();
          e.stopPropagation();
        }
        onConfirm();
      },
    });
  };

  const showDeleteConfirmationModal = (message: string, onConfirm: () => void) => {
    setCustomModal({
      isOpen: true,
      title: "Delete Confirmation",
      message,
      type: "confirmation",
      onConfirm: (e?: React.MouseEvent) => {
        if (e) {
          e.preventDefault();
          e.stopPropagation();
        }
        onConfirm();
      },
    });
  };

  const handleDeleteSharePoint = (sharepointId: number) => {
    const warningMessage = 'Are you sure you want to delete sharepoint source? This action cannot be undone.';
    showDeleteConfirmationModal(warningMessage, () => {
      setCustomModal({
        isOpen: true,
        title: "Deleting...",
        message: warningMessage,
        type: "loading",
      });

      deleteSharePoint(sharepointId, () => {
        setCustomModal({
          isOpen: true,
          title: "Success!",
          message: 'SharePoint Source deleted successfully',
          type: "success",
        });
      });
    });
  };

  const handleClose = () => {
    // Prevent closing during refresh operation
    if (isRefreshing) {
      setCustomModal({
        isOpen: true,
        title: "Refresh in Progress",
        message: "Please wait for the refresh operation to complete before closing.",
        type: "warning",
        onConfirm: () => setCustomModal(prev => ({ ...prev, isOpen: false }))
      });
      return;
    }

    const hasConnectedSharepoints = sharepoints?.some(s => s.connectionState === "connected");
    const hasUnsavedChanges = !pipelineData || pipelineData.length === 0 || pendingSave;

    if (hasConnectedSharepoints && hasUnsavedChanges) {
      showConfirmationModal(
        "Your changes have not been saved. Are you sure you want to close?", 
        "Save Confirmation", 
        () => {
          setCustomModal((prev) => ({ ...prev, isOpen: false }));
          setSharePointModalOpen(false);
        }
      );
    } else {
      setSharePointModalOpen(false);
    }
  };

  const handleRefreshNightlyChange = () => {
    if (!isSharePointRNightly) {
      if (assistant?.app_publishstatus?.toUpperCase() === 'UNPUBLISHED')
        setModalRefreshNModal(true);
    }
    setIsSharePointRNightly(!isSharePointRNightly);
    triggerAutoSave();
  };

  // NEW: Refresh handler with confirmation modal
  const handleRefresh = () => {
    if (!selectedSharePoint || !selectedSharepointData) {
      console.error('No SharePoint selected for refresh');
      return;
    }

    showConfirmationModal(
      "This action will fetch the SharePoint data again, and it might take a few minutes. Do you want to proceed?",
      "Refresh Preview Confirmation", 
      async () => {
        try {
          // Lock the modal to prevent closing
          setIsModalLocked(true);
          
          // Close confirmation modal and show loading modal
          setCustomModal({
            isOpen: true,
            title: "Refreshing SharePoint Data...",
            message: "Please wait while we fetch the latest data from SharePoint. This may take a few minutes.",
            type: "loading"
          });
          
          await handleRefreshSharePointData(selectedSharePoint);
          
          // Show success feedback
          setCustomModal({
            isOpen: true,
            title: "Success!",
            message: 'SharePoint data refreshed successfully',
            type: "success",
            onConfirm: (e?: React.MouseEvent) => {
              if (e) {
                e.preventDefault();
                e.stopPropagation();
              }
              setCustomModal(prev => ({ ...prev, isOpen: false }));
              setIsModalLocked(false); // Unlock modal
            }
          });
          
        } catch (error) {
          console.error('Refresh failed:', error);
          
          // Show error feedback
          setCustomModal({
            isOpen: true,
            title: "Refresh Failed",
            message: error instanceof Error ? error.message : 'Failed to refresh SharePoint data. Please try again.',
            type: "error",
            onConfirm: (e?: React.MouseEvent) => {
              if (e) {
                e.preventDefault();
                e.stopPropagation();
              }
              setCustomModal(prev => ({ ...prev, isOpen: false }));
              setIsModalLocked(false); // Unlock modal
            }
          });
        }
      }
    );
  };

  const handleItemClickWithValidation = (
    item: any, 
    itemType: 'lists' | 'document_libraries', 
    sharepointId: number
  ) => {
    if (!item || !itemType || !sharepointId) {
      console.error('Invalid parameters for item click:', { item, itemType, sharepointId });
      return;
    }

    const sharepoint = sharepoints.find(s => s.id === sharepointId);
    if (!sharepoint) {
      console.error('SharePoint not found:', sharepointId);
      return;
    }

    if (sharepoint.connectionState !== 'connected') {
      showConfirmationModal(
        "This SharePoint source is not connected. Please connect first before viewing items.",
        "Connection Required",
        () => {
          setCustomModal(prev => ({ ...prev, isOpen: false }));
          setActiveTab(sharepointId, 'CONNECT');
        }
      );
      return;
    }

    handleItemClick(item, itemType, sharepointId);
  };

  const handleBackToListsWithValidation = (sharepointId: number) => {
    if (!sharepointId) {
      console.error('Invalid SharePoint ID for back navigation:', sharepointId);
      return;
    }

    handleBackToLists(sharepointId);
  };

  const handleNavigateNextWithValidation = async (sharepointId: number): Promise<void> => {
    if (!sharepointId) {
      console.error('Invalid SharePoint ID for next navigation:', sharepointId);
      return;
    }

    const sharepoint = sharepoints.find(s => s.id === sharepointId);
    if (!sharepoint) {
      console.error('SharePoint not found for next navigation:', sharepointId);
      return;
    }

    if (!sharepoint.drillDownState?.isViewingItems) {
      console.error('Not in items view for next navigation:', sharepointId);
      return;
    }

    try {
      await handleNavigateNext(sharepointId);
    } catch (error) {
      console.error('Next navigation failed:', error);
      
      setCustomModal({
        isOpen: true,
        title: "Navigation Error",
        message: `Failed to load next page. Please try again.`,
        type: "error",
        onConfirm: () => setCustomModal(prev => ({ ...prev, isOpen: false }))
      });
    }
  };

  const handleNavigatePreviousWithValidation = async (sharepointId: number): Promise<void> => {
    if (!sharepointId) {
      console.error('Invalid SharePoint ID for previous navigation:', sharepointId);
      return;
    }

    const sharepoint = sharepoints.find(s => s.id === sharepointId);
    if (!sharepoint) {
      console.error('SharePoint not found for previous navigation:', sharepointId);
      return;
    }

    if (!sharepoint.drillDownState?.isViewingItems) {
      console.error('Not in items view for previous navigation:', sharepointId);
      return;
    }

    try {
      await handleNavigatePrevious(sharepointId);
    } catch (error) {
      console.error('Previous navigation failed:', error);
      
      setCustomModal({
        isOpen: true,
        title: "Navigation Error",
        message: `Failed to load previous page. Please try again.`,
        type: "error",
        onConfirm: () => setCustomModal(prev => ({ ...prev, isOpen: false }))
      });
    }
  };

  const handlePageSizeChangeWithValidation = async (
    sharepointId: number, 
    pageSize: number
  ): Promise<void> => {
    if (!sharepointId || !pageSize || pageSize < 1) {
      console.error('Invalid parameters for page size change:', { sharepointId, pageSize });
      return;
    }

    const sharepoint = sharepoints.find(s => s.id === sharepointId);
    if (!sharepoint) {
      console.error('SharePoint not found for page size change:', sharepointId);
      return;
    }

    try {
      await handlePageSizeChange(sharepointId, pageSize);
    } catch (error) {
      console.error('Page size change failed:', error);
      
      setCustomModal({
        isOpen: true,
        title: "Error",
        message: `Failed to change page size to ${pageSize}. Please try again.`,
        type: "error",
        onConfirm: () => setCustomModal(prev => ({ ...prev, isOpen: false }))
      });
    }
  };

  const handleSearchWithValidation = async (
    sharepointId: number,
    searchTerm: string,
    searchFields?: string[]
  ): Promise<void> => {
    if (!sharepointId) {
      console.error('Invalid SharePoint ID for search:', sharepointId);
      return;
    }

    const sharepoint = sharepoints.find(s => s.id === sharepointId);
    if (!sharepoint) {
      console.error('SharePoint not found for search:', sharepointId);
      return;
    }

    try {
      await handleSearch(sharepointId, searchTerm, searchFields);
    } catch (error) {
      console.error('Search failed:', error);
      
      setCustomModal({
        isOpen: true,
        title: "Search Error",
        message: `Search failed. Please try again.`,
        type: "error",
        onConfirm: () => setCustomModal(prev => ({ ...prev, isOpen: false }))
      });
    }
  };

  const safeSelectedSharepointData = selectedSharepointData || null;

  return (
    <div
      className="fixed inset-0 bg-gray-900 bg-opacity-50 flex justify-center items-center z-60"
      onClick={(e) => {
        e.stopPropagation();
        // Prevent closing during refresh or when modal is locked
        if (!isRefreshing && !isModalLocked) {
          setSharePointModalOpen(false);
        }
      }}
    >
      <div
        className="bg-white w-[90%] h-[80%] shadow-lg rounded-lg flex flex-col"
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
        }}
      >
        <SharePointHeader
          systemConfig={systemConfig}
          onClose={handleClose}
        />

        <div className="overflow-auto mt-[2%] h-full">
          <div className="flex flex-1 min-h-[85%]">
            <SharePointSidebar
              sharepoints={sharepoints}
              setSharepoints={setSharepoints}
              selectedSharePoint={selectedSharePoint}
              setSelectedSharePoint={setSelectedSharePoint}
              systemConfig={systemConfig}
              contextMenu={contextMenu}
              setContextMenu={setContextMenu}
              onAddNewSharePoint={addNewSharePoint}
              onMoveSharePoint={moveSharePoint}
              onDeleteSharePoint={handleDeleteSharePoint}
              onNameChange={handleNameChange}
              onDragStart={handleDragStart}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragEnd={handleDragEnd}
              onDragOver={handleDragOver}
              onContextMenu={handleContextMenu}
            />

            <div className="flex flex-col h-full w-full p-2">
              <div className="flex-shrink-0">
                <div className="border-b border-gray-200">
                  <div className="flex px-6 justify-between items-center">
                    <div className="flex">
                      <button
                        className={`px-4 py-2 border-b-2 font-medium transition-colors ${
                          safeSelectedSharepointData?.activeTab === 'CONNECT'
                            ? 'border-black text-black'
                            : 'border-transparent text-gray-500 hover:text-gray-700'
                        }`}
                        onClick={() => selectedSharePoint && setActiveTab(selectedSharePoint, 'CONNECT')}
                        disabled={!selectedSharePoint}
                      >
                        CONNECT
                      </button>
                      <button
                        className={`px-4 py-2 border-b-2 font-medium transition-colors ${
                          safeSelectedSharepointData?.activeTab === 'PREVIEW'
                            ? 'border-black text-black'
                            : safeSelectedSharepointData?.connectionState === 'connected'
                              ? 'border-transparent text-gray-500 hover:text-gray-700'
                              : 'border-transparent text-gray-300 cursor-not-allowed'
                        }`}
                        onClick={() => {
                          if (selectedSharePoint && safeSelectedSharepointData?.connectionState === 'connected') {
                            setActiveTab(selectedSharePoint, 'PREVIEW');
                          }
                        }}
                        disabled={!selectedSharePoint || safeSelectedSharepointData?.connectionState !== 'connected'}
                        title={
                          safeSelectedSharepointData?.connectionState !== 'connected' 
                            ? 'Connect to SharePoint first to preview data'
                            : 'Preview SharePoint data'
                        }
                      >
                        PREVIEW
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto">
                {safeSelectedSharepointData?.activeTab === 'CONNECT' && (
                  <SharePointConnectionTab
                    selectedSharepointData={safeSelectedSharepointData}
                    onUrlChange={handleUrlChange}
                    onConnect={handleConnect}
                    systemConfig={systemConfig}
                    assistant={assistant}
                  />
                )}

                {safeSelectedSharepointData?.activeTab === 'PREVIEW' && safeSelectedSharepointData && (
                  <SharePointPreviewTab
                    selectedSharepointData={safeSelectedSharepointData}
                    activeSubTabs={activeSubTabs}
                    setActiveSubTabs={setActiveSubTabs}
                    checkedItems={checkedItems}
                    isLoadingTabs={isLoadingTabs}
                    showDocumentCache={showDocumentCache}
                    isRefreshing={isRefreshing}
                    onRefresh={handleRefresh}
                    onCheckboxChange={handleCheckboxChange}
                    onToggleChange={handleInsideToggleChange}
                    updateExcludedItems={updateExcludedItems}
                    onItemClick={handleItemClickWithValidation}
                    onBackToLists={handleBackToListsWithValidation}
                    onNavigateNext={handleNavigateNextWithValidation}
                    onNavigatePrevious={handleNavigatePreviousWithValidation}
                    onPageSizeChange={handlePageSizeChangeWithValidation}
                    onDocumentExclusion={handleDocumentExclusion}
                    onLibraryExclusion={handleLibraryExclusion}
                    isDocumentExcluded={isDocumentExcluded}
                    getLibrarySelectionState={getLibrarySelectionState}
                  />
                )}

                {!safeSelectedSharepointData && (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center">
                      <h3 className="text-xl font-semibold mb-2 text-gray-500">
                        Welcome to SharePoint Integration
                      </h3>
                      <p className="text-gray-400 mb-4">
                        Select a SharePoint source from the sidebar or create a new one to get started.
                      </p>
                      <button
                        onClick={addNewSharePoint}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                      >
                        Create New SharePoint Connection
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <SharePointFooter
            sharepoints={sharepoints}
            isSharePointRNightly={isSharePointRNightly}
            setIsSharePointRNightly={setIsSharePointRNightly}
            currentStatus={currentStatus}
            pipelineData={pipelineData}
            expandedGetData={expandedGetData}
            setExpandedGetData={setExpandedGetData}
            expandedRows={expandedRows}
            toggleExpandRow={toggleExpandRow}
            pendingSave={pendingSave}
            gettingData={gettingData}
            lastSaveTarget={lastSaveTarget}
            assistant={assistant}
            onRefreshNightlyChange={handleRefreshNightlyChange}
            onGetDataNow={handleGetDataNow}
            onCancelPipeline={handleCancelPipeline}
            cancellingPipeline={cancellingPipeline}
          />
        </div>
      </div>

      <SharePointModals
        customModal={customModal}
        setCustomModal={setCustomModal}
        modalRefreshNModal={modalRefreshNModal}
        setModalRefreshNModal={setModalRefreshNModal}
      />
    </div>
  );
};

export default SharePoint;